// Repositorio (GET /documents/list): todos los documentos de la compañía con
// filtros por tipo, estado efectivo, rango de fechas y texto; descarga directa.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Spinner } from "../../../components/ui";
import { FilterSelect, LoadBlock, SearchBox, Toolbar, inputCls } from "../../bases-propiedades/components/console";
import { DOCUMENT_STATUSES, DOCUMENT_TYPE_IDS, documentTypeName, type DocumentStatus } from "../../../utils/documentTypes";
import { documentsErrorMessage } from "../../../services/shipmentDocuments";
import { DOCS_NS, PROVIDER_ICONS, shortDate, shortDateTime } from "../../../features/shipmentDocuments/meta";
import { ExpiryChip, StatusChip } from "../../../features/shipmentDocuments/DocumentChecklist";
import { openDownload } from "../../../features/shipmentDocuments/hooks";
import { useCompanyList } from "../queries";
import { SubjectCell, queryStatus } from "../components/pieces";

export default function RepositorioSection() {
  const { t, i18n } = useTranslation(DOCS_NS);
  const [type, setType] = useState<string>("");
  const [status, setStatus] = useState<DocumentStatus | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    const h = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(h);
  }, [q]);

  const query = useCompanyList({
    type: type || undefined,
    status: status || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
    q: debounced || undefined,
    limit: 500,
  });
  const rows = useMemo(() => query.data ?? [], [query.data]);
  const qs = queryStatus(query);

  const typeOptions = useMemo(() => DOCUMENT_TYPE_IDS.map((id) => ({ value: id, label: documentTypeName(id, i18n.language) })).sort((a, b) => a.label.localeCompare(b.label)), [i18n.language]);

  const download = async (id: string) => {
    setDownloading(id);
    try {
      await openDownload(id);
    } catch (err) {
      toast.error(documentsErrorMessage(err, t("checklist.download.failed")));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <Toolbar>
        <div className="flex items-center gap-2">
          <i className="ri-archive-drawer-line text-lg text-primary-500" />
          <h1 className="text-sm font-semibold text-content">{t("repositorio.title")}</h1>
          <span className="text-xs text-content-muted">{t("repositorio.count", { n: rows.length })}</span>
        </div>
        <SearchBox value={q} onChange={setQ} placeholder={t("repositorio.filters.search")} />
        <FilterSelect<string> value={type} onChange={(v) => setType(v)} label={t("repositorio.filters.type")} options={typeOptions} />
        <FilterSelect<DocumentStatus> value={status} onChange={(v) => setStatus(v)} label={t("repositorio.filters.status")} options={DOCUMENT_STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) }))} />
        <label className="flex items-center gap-1 text-xs text-content-secondary">
          {t("repositorio.filters.from")}
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} !w-auto !py-1.5`} aria-label={t("repositorio.filters.from")} />
        </label>
        <label className="flex items-center gap-1 text-xs text-content-secondary">
          {t("repositorio.filters.to")}
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} !w-auto !py-1.5`} aria-label={t("repositorio.filters.to")} />
        </label>
        <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={() => void query.refetch()} aria-label={t("common.refresh")} title={t("common.refresh")} iconOnly />
      </Toolbar>
      <div className="flex-1 overflow-y-auto p-3">
        <LoadBlock status={qs.status} error={qs.error} onRetry={qs.retry} empty={rows.length === 0} emptyIcon="ri-archive-drawer-line" emptyTitle={t("repositorio.empty")} emptyText={t("repositorio.emptyText")}>
          <div className="overflow-hidden rounded-xl border border-line bg-card">
            <table className="w-full text-sm" data-testid="repository-table">
              <thead>
                <tr className="border-b border-line bg-subtle/60 text-left text-[11px] uppercase tracking-wide text-content-secondary">
                  <th className="px-3 py-2 font-semibold">{t("repositorio.columns.document")}</th>
                  <th className="px-3 py-2 font-semibold">{t("repositorio.columns.subject")}</th>
                  <th className="px-3 py-2 font-semibold">{t("repositorio.columns.status")}</th>
                  <th className="hidden px-3 py-2 font-semibold md:table-cell">{t("repositorio.columns.provider")}</th>
                  <th className="hidden px-3 py-2 font-semibold md:table-cell">{t("repositorio.columns.version")}</th>
                  <th className="hidden px-3 py-2 font-semibold lg:table-cell">{t("repositorio.columns.updated")}</th>
                  <th className="hidden px-3 py-2 font-semibold lg:table-cell">{t("repositorio.columns.expires")}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((d) => {
                  const v = d.versions[d.versions.length - 1];
                  return (
                    <tr key={d.id} className="hover:bg-subtle">
                      <td className="max-w-[16rem] px-3 py-2">
                        <div className="truncate font-medium text-content">{documentTypeName(d.doc_type, i18n.language, d.name)}</div>
                        <div className="truncate text-[11px] text-content-muted">{v?.file_name || d.reference || t("checklist.noFile")}</div>
                      </td>
                      <td className="max-w-[14rem] px-3 py-2">
                        <SubjectCell subject={d.subject} label={d.subject.id} secondary={t(`kind.${d.subject.kind}`)} />
                      </td>
                      <td className="px-3 py-2">
                        <StatusChip status={d.effective_status} />
                      </td>
                      <td className="hidden px-3 py-2 text-xs text-content-secondary md:table-cell">
                        <i className={PROVIDER_ICONS[d.provided_by]} /> {t(`provider.${d.provided_by}`)}
                      </td>
                      <td className="hidden px-3 py-2 text-xs tabular-nums text-content-secondary md:table-cell">{d.has_file ? `v${d.current_version}` : "—"}</td>
                      <td className="hidden px-3 py-2 text-xs text-content-secondary lg:table-cell">{shortDateTime(d.updated_at, i18n.language)}</td>
                      <td className="hidden px-3 py-2 text-xs lg:table-cell">{d.expires_at ? <ExpiryChip days={d.expires_in_days} expiresAt={d.expires_at} /> : <span className="text-content-muted">{shortDate(null, i18n.language)}</span>}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" disabled={!d.has_file || downloading === d.id} onClick={() => void download(d.id)} aria-label={t("common.download")} title={t("common.download")} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle hover:text-content disabled:opacity-40">
                          {downloading === d.id ? <Spinner size="sm" /> : <i className="ri-download-2-line" />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LoadBlock>
      </div>
    </div>
  );
}
